import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard, { tableHeaderCellSx } from "./components/DataCard";
import LicenseAgreementText from "./LicenseAgreementText";
import { useLicenseAgreementsStore } from "./store/licenseAgreementsStore";
import { useCompaniesStore } from "./store/companiesStore";

const EMPTY_AGREEMENT = {
  id: "",
  title: "",
  version: "",
  body: "",
  is_default: false,
  active: true,
};

const formatDate = (ms) => (ms ? new Date(ms).toLocaleString() : "—");

// Suggest the next id for a "new version" copy: acme-v1 → acme-v2, else append -v2.
function nextVersionId(id) {
  const m = /^(.*-v)(\d+)$/.exec(id || "");
  return m ? `${m[1]}${Number(m[2]) + 1}` : `${id}-v2`;
}

function AgreementsTab({ companies }) {
  const { agreements, isLoading, error, fetchAgreements, addAgreement, updateAgreement, deleteAgreement } =
    useLicenseAgreementsStore();
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [editorTab, setEditorTab] = useState("edit");
  const [saveError, setSaveError] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    fetchAgreements();
  }, [fetchAgreements]);

  const companiesByAgreement = useMemo(() => {
    const out = {};
    (companies || []).forEach((c) => {
      if (c.license_agreement_id) (out[c.license_agreement_id] ||= []).push(c.name || c.id);
    });
    return out;
  }, [companies]);

  const open = (agreement, asNew) => {
    setEditing({ ...EMPTY_AGREEMENT, ...agreement });
    setIsNew(asNew);
    setEditorTab("edit");
    setSaveError("");
  };

  const openNewVersion = (agreement) =>
    open({ ...agreement, id: nextVersionId(agreement.id), version: "", is_default: false, created: null, modified: null }, true);

  const close = () => setEditing(null);
  const handleField = (field, value) => setEditing((prev) => ({ ...prev, [field]: value }));

  const save = async () => {
    if (!editing.id?.trim()) {
      setSaveError("Agreement id is required, e.g. acme-v1.");
      return;
    }
    if (!editing.body?.trim()) {
      setSaveError("Agreement text is required.");
      return;
    }
    try {
      if (isNew) await addAgreement({ ...editing, id: editing.id.trim() });
      else await updateAgreement(editing.id, editing);
      close();
    } catch (e) {
      setSaveError(e?.message || "Failed to save license agreement");
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteAgreement(deleteId);
      setDeleteId(null);
    } catch (e) {
      setDeleteError(e?.message || "Failed to delete license agreement");
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => open(EMPTY_AGREEMENT, true)}>
          Add Agreement
        </Button>
      </Box>
      <DataCard noPadding>
        {isLoading && <Box sx={{ p: 3, color: "text.secondary" }}>Loading agreements…</Box>}
        {error && <Box sx={{ p: 3, color: "error.main" }}>Error: {error}</Box>}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderCellSx}>ID</TableCell>
              <TableCell sx={tableHeaderCellSx}>Title</TableCell>
              <TableCell sx={tableHeaderCellSx}>Version</TableCell>
              <TableCell sx={tableHeaderCellSx}>Status</TableCell>
              <TableCell sx={tableHeaderCellSx}>Applies to</TableCell>
              <TableCell sx={tableHeaderCellSx}>Modified</TableCell>
              <TableCell sx={tableHeaderCellSx} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agreements.map((a) => {
              const assigned = companiesByAgreement[a.id] || [];
              return (
                <TableRow key={a.id} hover>
                  <TableCell>{a.id}</TableCell>
                  <TableCell>{a.title}</TableCell>
                  <TableCell>{a.version}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {a.is_default && <Chip label="default" size="small" color="primary" />}
                      {a.active === false ? (
                        <Chip label="inactive" size="small" />
                      ) : (
                        <Chip label="active" size="small" color="success" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {a.is_default && (
                      <Typography variant="body2">Everyone without a company agreement</Typography>
                    )}
                    {assigned.length > 0 && <Typography variant="body2">{assigned.join(", ")}</Typography>}
                    {!a.is_default && assigned.length === 0 && (
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>Not assigned</Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(a.modified)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => open(a, false)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="New version (copy)">
                      <IconButton size="small" onClick={() => openNewVersion(a)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={a.is_default ? "The default agreement can't be deleted" : "Delete"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={a.is_default}
                          onClick={() => {
                            setDeleteError("");
                            setDeleteId(a.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DataCard>

      <Dialog open={!!editing} onClose={close} maxWidth="lg" fullWidth>
        <DialogTitle>{isNew ? "Add License Agreement" : `Edit License Agreement — ${editing?.id}`}</DialogTitle>
        <DialogContent>
          {editing && (
            <>
              {!isNew && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Users who already accepted this agreement are <strong>not</strong> asked to accept edited
                  text again. For a substantive change, use <em>New version</em> instead and assign it to the
                  company (or make it the default).
                </Alert>
              )}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 2 }}>
                <TextField
                  label="ID"
                  value={editing.id}
                  onChange={(e) => handleField("id", e.target.value)}
                  margin="normal"
                  required
                  disabled={!isNew}
                  helperText="Include the version, e.g. acme-v1"
                />
                <TextField
                  label="Title"
                  value={editing.title || ""}
                  onChange={(e) => handleField("title", e.target.value)}
                  margin="normal"
                />
                <TextField
                  label="Version"
                  value={editing.version || ""}
                  onChange={(e) => handleField("version", e.target.value)}
                  margin="normal"
                />
              </Box>
              <Box sx={{ display: "flex", gap: 3 }}>
                <FormControlLabel
                  control={
                    <Switch checked={editing.active !== false} onChange={(_, v) => handleField("active", v)} />
                  }
                  label="Active"
                />
                <Tooltip title="The agreement for every user whose company has none assigned. Only one agreement can be the default.">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!editing.is_default}
                        onChange={(_, v) => handleField("is_default", v)}
                      />
                    }
                    label="Default agreement"
                  />
                </Tooltip>
              </Box>
              <Tabs value={editorTab} onChange={(_, v) => setEditorTab(v)} sx={{ mt: 1 }}>
                <Tab value="edit" label="Text (Markdown)" />
                <Tab value="preview" label="Preview" />
              </Tabs>
              {editorTab === "edit" ? (
                <TextField
                  value={editing.body || ""}
                  onChange={(e) => handleField("body", e.target.value)}
                  fullWidth
                  multiline
                  minRows={18}
                  margin="normal"
                  placeholder={"**1. License Grant**\n\nLicensor grants you ..."}
                  InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.85rem" } }}
                />
              ) : (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    maxHeight: 480,
                    overflowY: "auto",
                    backgroundColor: "#f8f8f8",
                    border: "1px solid #ddd",
                    borderRadius: 1,
                  }}
                >
                  <LicenseAgreementText agreement={editing} />
                </Box>
              )}
              {saveError && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSaveError("")}>
                  {saveError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button onClick={save} variant="contained">{isNew ? "Create" : "Save"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete License Agreement?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteId}</strong>? This cannot be undone. Existing acceptance records are kept.
          </DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function AcceptanceTab() {
  const { status, statusSummary, isLoading, error, fetchStatus } = useLicenseAgreementsStore();
  const [agreementFilter, setAgreementFilter] = useState("all");
  const [pendingOnly, setPendingOnly] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const rows = status.filter(
    (r) => (agreementFilter === "all" || r.agreement_id === agreementFilter) && (!pendingOnly || !r.accepted)
  );

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        {Object.entries(statusSummary).map(([id, s]) => (
          <DataCard key={id} sx={{ width: "auto", minWidth: 220 }}>
            <Typography variant="subtitle2">{s.title || id}</Typography>
            <Typography variant="caption" color="text.secondary">{id}</Typography>
            <Typography variant="h6">
              {s.accepted} / {s.users} accepted
            </Typography>
          </DataCard>
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <Select size="small" value={agreementFilter} onChange={(e) => setAgreementFilter(e.target.value)}>
          <MenuItem value="all">All agreements</MenuItem>
          {Object.keys(statusSummary).map((id) => (
            <MenuItem key={id} value={id}>{id}</MenuItem>
          ))}
        </Select>
        <FormControlLabel
          control={<Switch checked={pendingOnly} onChange={(_, v) => setPendingOnly(v)} />}
          label="Not yet accepted only"
        />
      </Box>
      <DataCard noPadding>
        {isLoading && <Box sx={{ p: 3, color: "text.secondary" }}>Loading…</Box>}
        {error && <Box sx={{ p: 3, color: "error.main" }}>Error: {error}</Box>}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderCellSx}>User</TableCell>
              <TableCell sx={tableHeaderCellSx}>Company</TableCell>
              <TableCell sx={tableHeaderCellSx}>Agreement</TableCell>
              <TableCell sx={tableHeaderCellSx}>Accepted</TableCell>
              <TableCell sx={tableHeaderCellSx}>IP address</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.email} hover>
                <TableCell>
                  {r.email}
                  {r.fullname && (
                    <Typography variant="caption" component="div" color="text.secondary">{r.fullname}</Typography>
                  )}
                </TableCell>
                <TableCell>{r.company_name || r.company_id || "—"}</TableCell>
                <TableCell>{r.agreement_id || "—"}</TableCell>
                <TableCell>
                  {r.accepted ? (
                    <Tooltip title={r.legacy ? "Accepted before per-agreement tracking (no IP/user-agent recorded)" : ""}>
                      <span>
                        {r.accepted_at ? formatDate(r.accepted_at) : "yes"}
                        {r.legacy && " (legacy)"}
                      </span>
                    </Tooltip>
                  ) : (
                    <Chip label="pending" size="small" color="warning" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>{r.ip_address || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataCard>
    </>
  );
}

export default function LicenseAgreements() {
  const [tab, setTab] = useState("agreements");
  const { companies, fetchCompanies } = useCompaniesStore();

  useEffect(() => {
    fetchCompanies(true);
  }, [fetchCompanies]);

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<GavelIcon />}
          title="License Agreements"
          subtitle="End user license agreements, which companies get which, and who has accepted"
        />
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab value="agreements" label="Agreements" />
          <Tab value="acceptance" label="Acceptance" />
        </Tabs>
        {tab === "agreements" ? <AgreementsTab companies={companies} /> : <AcceptanceTab />}
      </PageContainer>
    </PageShell>
  );
}
